import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { AlertCircle } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-conditions-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-4">Terms and Conditions</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 29, 2025</p>
          
          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <p className="text-lg leading-relaxed">
                Welcome to Xtream Store. By accessing or purchasing from our website, you agree to be bound by the following Terms and Conditions. If you do not agree, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">1. About Xtream Store</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Xtream Store is a digital marketplace that provides access to digital products such as OTT subscriptions and other online services at discounted prices.</li>
                <li>We are not officially affiliated with any OTT platform or service provider unless explicitly mentioned.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">2. Eligibility</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Users must be 18 years or older to use this website</li>
                <li>By placing an order, you confirm that you are legally eligible to do so</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">3. Nature of Digital Products</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>All products sold are digital in nature</li>
                <li>No physical goods are shipped</li>
                <li>Delivery details are shared digitally via email, dashboard, or messaging platforms</li>
              </ul>
            </section>

            <section className="bg-primary/10 p-6 rounded-xl border border-primary/20">
              <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-primary" />
                4. Warranty / Validity Policy (IMPORTANT)
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Most accounts are sold with a validity period that acts as a warranty</li>
                <li>The validity mentioned on the product page is the warranty period</li>
                <li>Warranty starts from the time of delivery</li>
              </ul>
            </section>

            <section className="bg-destructive/10 p-6 rounded-xl border border-destructive/20">
              <h2 className="text-2xl font-heading font-bold mb-4 text-destructive">5. No Refund Policy (Strict)</h2>
              <p className="text-xl font-bold mb-4">🚫 NO REFUNDS — ONLY REPLACEMENT</p>
              <p className="mb-2">Once a product is delivered, no refunds will be provided under any circumstances</p>
              <p className="mb-2">This includes:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Change of mind</li>
                <li>Accidental purchase</li>
                <li>Compatibility issues</li>
                <li>User error</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">6. Replacement Policy</h2>
              <p className="font-bold mb-2">Replacement is allowed only if:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                <li>The account stops working within the warranty / validity period</li>
                <li>The issue is not caused by the user</li>
              </ul>
              
              <p className="font-bold mb-2">Replacement Conditions:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                <li>You must report the issue within the warranty period</li>
                <li>Proper proof (screenshots / screen recording) may be required</li>
                <li>Replacement will be provided once per order, unless otherwise stated</li>
                <li>Replacement may be: Same product, Equivalent product, or Extended validity</li>
              </ul>
              
              <p className="font-bold mb-2 text-destructive">❌ No replacement if:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Password/email is changed by user</li>
                <li>Account is shared beyond allowed limits</li>
                <li>Login attempts exceed limits</li>
                <li>Terms of the service are violated</li>
                <li>Device or IP misuse is detected</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">7. User Responsibilities</h2>
              <p>Users agree:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Not to change account credentials unless allowed</li>
                <li>Not to share, resell, or misuse accounts</li>
                <li>To use accounts only as described in the product details</li>
              </ul>
              <p className="text-sm text-destructive mt-2">Violation may result in immediate termination without replacement.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">8. Delivery Timeline</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Most orders are delivered instantly or within the stated delivery time</li>
                <li>Delays due to technical or external factors do not qualify for refunds</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">9. Account Suspension or Termination</h2>
              <p>Xtream Store reserves the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-2">
                <li>Suspend or terminate access</li>
                <li>Cancel replacement eligibility</li>
              </ul>
              <p>If:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Fraudulent activity is detected</li>
                <li>Terms are violated</li>
                <li>Abuse of support or replacement policy occurs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">10. Limitation of Liability</h2>
              <p>Xtream Store shall not be liable for:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Loss of data</li>
                <li>Business interruption</li>
                <li>Account bans due to user actions</li>
                <li>Changes made by OTT platforms</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">Use of services is at the user's own risk.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">11. Modifications to Services</h2>
              <p>Xtream Store may:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Modify or discontinue products</li>
                <li>Update prices or validity</li>
                <li>Change Terms & Conditions at any time</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">Continued use means acceptance of updated terms.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">12. Governing Law</h2>
              <p>These Terms shall be governed in accordance with the laws applicable in your jurisdiction.</p>
            </section>

            <section className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h2 className="text-2xl font-heading font-bold mb-4">13. Contact & Support</h2>
              <p>For support or replacement requests, contact us via:</p>
              <div className="mt-4 space-y-2">
                <p className="flex items-center gap-2">
                  <span className="font-bold">Email:</span>
                  <a href="mailto:Support@xtreamstore.in" className="text-primary hover:underline">
                    Support@xtreamstore.in
                  </a>
                </p>
                <p><span className="font-bold">Platform:</span> Xtream Store</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsConditions;